import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getInviteByToken, submitResponse } from '../services/review360Service';
import { fetchTemplate } from '../services/reviewTemplateService';
import { fetchCycle } from '../services/reviewCycleService';
import type { Review360Invite, ReviewTemplate, OpticsCriteria } from '../types';

const Review360Form: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { signInAnon, firebaseUser } = useAuth();
  const [invite, setInvite] = useState<Review360Invite | null>(null);
  const [template, setTemplate] = useState<ReviewTemplate | null>(null);
  const [opticsQScores, setOpticsQScores] = useState<Record<string, number[]>>({});
  const [state, setState] = useState<'loading' | 'form' | 'submitted' | 'invalid' | 'expired' | 'done'>('loading');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const init = async () => {
      try {
        if (!token) { setState('invalid'); return; }

        // Ensure anonymous auth
        if (!firebaseUser) await signInAnon();

        const inv = await getInviteByToken(token);
        if (!inv) { setState('invalid'); return; }
        if (inv.status === 'completed') { setState('done'); return; }
        if (inv.status === 'expired') { setState('expired'); return; }
        // Check expiry. Firestore Timestamp in source — could arrive as
        // a Timestamp instance or as a serialised { seconds, nanoseconds }
        // object after a JSON round-trip. Narrow once, not three times.
        const exp = inv.expiresAt as { toDate?: () => Date; seconds?: number };
        const expDate = typeof exp.toDate === 'function'
          ? exp.toDate()
          : new Date((exp.seconds ?? 0) * 1000);
        if (expDate < new Date()) { setState('expired'); return; }

        setInvite(inv);

        // Load cycle + template
        const cycle = await fetchCycle(inv.cycleId);
        if (cycle) {
          const t = await fetchTemplate(cycle.templateId);
          setTemplate(t);
          if (t) {
            const initScores: Record<string, number[]> = {};
            t.opticsCriteria.forEach(c => { initScores[c.name] = Array(c.questions.length).fill(0); });
            setOpticsQScores(initScores);
          }
        }

        setState('form');
      } catch (e) {
        setError(String(e));
        setState('invalid');
      }
    };
    init();
  }, [token, firebaseUser, signInAnon]);

  const handleSubmit = async () => {
    if (!invite || !template) return;

    // Validate all questions answered
    const unanswered = template.opticsCriteria.filter(c =>
      (opticsQScores[c.name] ?? []).some(s => s === 0),
    );
    if (unanswered.length) {
      setError(`Please answer all questions for: ${unanswered.map(c => c.name).join(', ')}`);
      return;
    }

    setBusy(true);
    setError('');
    try {
      // Average question scores per criteria
      const aggregated: Record<string, number> = {};
      template.opticsCriteria.forEach(c => {
        const qs = opticsQScores[c.name] ?? [];
        aggregated[c.name] = qs.length ? qs.reduce((a, b) => a + b, 0) / qs.length : 0;
      });

      await submitResponse(invite, aggregated);
      setState('submitted');
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  // ─── States ───────────────────────────────────────────────────────────────

  if (state === 'loading') {
    return <CenteredCard><div className="animate-pulse text-brand-accent">Loading your 360° form…</div></CenteredCard>;
  }

  if (state === 'invalid') {
    return <CenteredCard><ErrorState icon="❌" title="Invalid Link" message="This link is not valid. Please request a new invite." /></CenteredCard>;
  }

  if (state === 'expired') {
    return <CenteredCard><ErrorState icon="⏰" title="Link Expired" message="This 360° review link has expired. Please contact HR." /></CenteredCard>;
  }

  if (state === 'done') {
    return <CenteredCard><ErrorState icon="✅" title="Already Submitted" message="You have already submitted your feedback for this review." /></CenteredCard>;
  }

  if (state === 'submitted') {
    return (
      <CenteredCard>
        <div className="text-center space-y-3">
          <div className="text-5xl">🙏</div>
          <h2 className="text-xl font-bold text-gray-900">Thank you!</h2>
          <p className="text-gray-500 text-sm">
            Your 360° feedback for <strong className="text-gray-900">{invite?.revieweeName}</strong> has been submitted.
          </p>
          <p className="text-gray-400 text-xs">Your response is anonymous and will only be seen in aggregate.</p>
        </div>
      </CenteredCard>
    );
  }

  if (!template || !invite) return null;

  // ─── Form ─────────────────────────────────────────────────────────────────

  const totalCriteria = template.opticsCriteria.length;
  const answeredCriteria = template.opticsCriteria.filter(c =>
    (opticsQScores[c.name] ?? []).every(s => s > 0),
  ).length;

  return (
    <div className="min-h-screen bg-brand-primary p-4 md:p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center pt-4">
          <h1 className="text-2xl font-bold text-gray-900">360° Feedback Form</h1>
          <p className="text-gray-500 mt-1 text-sm">
            You're providing feedback for <strong className="text-gray-900">{invite.revieweeName}</strong>
          </p>
          <p className="text-gray-400 text-xs mt-1">Your response is anonymous and confidential.</p>
        </div>

        {/* Progress */}
        <div className="bg-white border border-gray-200 shadow-sm rounded-xl p-3 flex items-center gap-3">
          <div className="flex-1 bg-gray-200 rounded-full h-2">
            <div
              className="h-2 rounded-full bg-brand-accent transition-all"
              style={{ width: `${(answeredCriteria / totalCriteria) * 100}%` }}
            />
          </div>
          <span className="text-xs text-gray-500 shrink-0">{answeredCriteria}/{totalCriteria} sections</span>
        </div>

        {/* OPTICS criteria */}
        {template.opticsCriteria.map((criteria: OpticsCriteria) => {
          const qScores = opticsQScores[criteria.name] ?? [];
          const complete = qScores.every(s => s > 0);
          return (
            <div key={criteria.name} className={`bg-white border rounded-xl overflow-hidden ${complete ? 'border-brand-success/40' : 'border-gray-200'}`}>
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="font-semibold text-gray-900 text-sm">{criteria.name}</h2>
                {complete && <span className="text-brand-success text-xs">✓ Complete</span>}
              </div>
              <div className="p-4 space-y-4">
                {criteria.questions.map((q, qi) => (
                  <div key={qi}>
                    <p className="text-sm text-gray-700 mb-2">{q}</p>
                    <div className="flex gap-2 flex-wrap">
                      {[1, 2, 3, 4, 5].map(n => (
                        <button
                          key={n}
                          type="button"
                          onClick={() => {
                            const updated = [...(opticsQScores[criteria.name] ?? [])];
                            updated[qi] = n;
                            setOpticsQScores({ ...opticsQScores, [criteria.name]: updated });
                          }}
                          className={`w-10 h-10 rounded-full text-sm font-semibold border transition ${
                            qScores[qi] === n
                              ? 'bg-blue-600 border-blue-600 text-white'
                              : 'bg-transparent border-gray-300 text-gray-500 hover:border-gray-600 hover:text-gray-900'
                          }`}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                    <div className="flex justify-between text-xs text-gray-400 mt-1 px-1">
                      <span>Rarely</span>
                      <span>Consistently</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {error && <p className="text-brand-error text-sm">{error}</p>}

        <button
          onClick={handleSubmit}
          disabled={busy || answeredCriteria < totalCriteria}
          className="w-full bg-brand-accent hover:bg-blue-500 text-gray-900 font-bold py-3 rounded-xl text-sm transition disabled:opacity-40"
        >
          {busy ? 'Submitting…' : 'Submit Feedback'}
        </button>
        <p className="text-xs text-gray-400 text-center pb-6">
          By submitting you confirm your responses are honest and confidential.
        </p>
      </div>
    </div>
  );
};

const CenteredCard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-brand-primary flex items-center justify-center p-4">
    <div className="max-w-sm w-full bg-white border border-gray-200 shadow-sm rounded-2xl p-6">
      {children}
    </div>
  </div>
);

const ErrorState: React.FC<{ icon: string; title: string; message: string }> = ({ icon, title, message }) => (
  <div className="text-center space-y-3">
    <div className="text-4xl">{icon}</div>
    <h2 className="text-lg font-bold text-gray-900">{title}</h2>
    <p className="text-gray-500 text-sm">{message}</p>
  </div>
);

export default Review360Form;
